/**
 * The viewer's camera mode.
 *
 * `Viewer.tsx` held this as two independent booleans (`orthoCamera`,
 * `thirdPersonFlying`) for a long time. Four combinations encoding three modes
 * meant one of them was always a lie: ortho forced third person through
 * `thirdperson={thirdPersonFlying || orthoCamera}` while `thirdPersonFlying`
 * kept whatever it held before, so leaving ortho dropped the user back into a
 * mode they had not chosen — first person, whose orbit radius is pinned at ~0,
 * which reads as the camera falling into the ground.
 *
 * The mode is the source of truth. The booleans survive only as the persisted
 * shape: `StoredCameraState` in sessionStorage and `SavedViewCamera` in the
 * database both predate this and are read by clients that know nothing about
 * the enum, so the conversion goes through here rather than being inlined.
 */
export type CameraMode = 'FIRST_PERSON' | 'THIRD_PERSON' | 'ORTHO';

/** The persisted pair of flags. */
export interface CameraModeFlags {
  ortho: boolean;
  thirdPerson: boolean;
}

/** Ortho wins: it was always the effective mode whenever the flag was set. */
export function cameraModeFromFlags(flags: CameraModeFlags): CameraMode {
  if (flags.ortho) return 'ORTHO';
  return flags.thirdPerson ? 'THIRD_PERSON' : 'FIRST_PERSON';
}

/**
 * Ortho writes `thirdPerson: true` as well, so a reader that only understands
 * the booleans (an older client restoring a saved view) still gets an orbit
 * camera rather than a first-person one.
 */
export function cameraModeToFlags(mode: CameraMode): CameraModeFlags {
  switch (mode) {
    case 'ORTHO':
      return { ortho: true, thirdPerson: true };
    case 'THIRD_PERSON':
      return { ortho: false, thirdPerson: true };
    case 'FIRST_PERSON':
      return { ortho: false, thirdPerson: false };
  }
}

/** What `Controls` reads as `thirdperson`: ortho orbits its target too. */
export function isThirdPerson(mode: CameraMode): boolean {
  return mode !== 'FIRST_PERSON';
}

export function isOrtho(mode: CameraMode): boolean {
  return mode === 'ORTHO';
}

/**
 * The `O` hotkey / ortho segment. Leaving ortho returns to the perspective mode
 * the user was in when they entered it, which is the whole reason `Viewer`
 * remembers one.
 */
export function toggleOrtho(mode: CameraMode, previousPerspective: CameraMode = 'THIRD_PERSON'): CameraMode {
  if (mode !== 'ORTHO') return 'ORTHO';
  return previousPerspective === 'ORTHO' ? 'THIRD_PERSON' : previousPerspective;
}

/**
 * The `T` hotkey. From ortho it lands on third person — the orbiting
 * perspective mode, the closest thing to where you already were. The old UI
 * disabled the button in ortho instead, leaving `O` as the only way out.
 */
export function togglePerson(mode: CameraMode): CameraMode {
  return mode === 'THIRD_PERSON' ? 'FIRST_PERSON' : 'THIRD_PERSON';
}
